'use client';
import { useState } from 'react';
import FilterElement from './FilterElement';
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { Button, Checkbox, Label } from 'flowbite-react';
import { FilterableListItem } from './FilterableListbox-utils';
import StyledSpinner from './StyledSpinner';
import { ALL_STATUSES } from '@/lib/config';

interface FiltersDropdownProps {
    styles: FilterableListItem[],
    presets: FilterableListItem[],
    selectedStyle: string,
    selectedPreset: string,
    audioOnly: boolean,
    includePrivate: boolean,
    allStatuses: boolean,
    isAdmin: boolean,
}

export default function FiltersDropdown({ styles, presets, selectedStyle, selectedPreset, audioOnly, includePrivate, allStatuses, isAdmin }: FiltersDropdownProps) {
    const [isOpen, setIsOpen] = useState(false);
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();

    const [audioOnlyIsTicked, setAudioOnlyIsTicked] = useState(audioOnly);
    const [includePrivateIsTicked, setIncludePrivateIsTicked] = useState(isAdmin && includePrivate);
    const [allStatusesIsTicked, setAllStatusesIsTicked] = useState(isAdmin && allStatuses);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [loading, setLoading] = useState(false);

    const updateURLParam = (key: string, value: string) => {
        if (searchParams) {
            const current = new URLSearchParams(Array.from(searchParams.entries()));
            if (value === 'All') {
                current.delete(key);
            } else {
                current.set(key, value);
            }
            const search = current.toString();
            const query = search ? `?${search}` : "";
            router.push(`${pathname}${query}`);
            
            // TODO - exiting loading state depends on a different component completing
            // setLoading(true);
        }
    };

    const toggleDropdown = () => {
        setIsOpen(!isOpen);
    };

    return (
        <div className="relative inline-block text-left">
            <div>
                <Button
                    color={'primary'}
                    type="button"
                    onClick={toggleDropdown}
                >
                    <div className="flex items-center">
                        Filters
                        {(selectedStyle !== 'All' && selectedStyle !== '') || (selectedPreset !== 'All' && selectedPreset !== '') ? (
                            <span className="ml-2 h-2 w-2 rounded-full bg-blue-500"></span>
                        ) : null}
                        <svg
                            className={`-mr-1 ml-2 h-5 w-5 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            aria-hidden="true"
                        >
                            <path fillRule="evenodd" d="M10 12l-6-6h12l-6 6z" />
                        </svg>
                    </div>
                </Button>
            </div>

            {isOpen && (
                <div className="origin-top-right absolute right-0 mt-2 p-4 shadow-lg bg-neutral-200 focus:outline-none dark:bg-neutral-800 flex flex-col gap-4 z-10">
                    <FilterElement
                        label='Style'
                        options={styles}
                        name="Style"
                        value={selectedStyle === '' ? 'All' : selectedStyle}
                        hideFilter={false}
                        onChange={style => updateURLParam('styleFilter', style)}
                    />
                    <FilterElement
                        label='Motion preset'
                        options={presets}
                        name="Motion preset"
                        value={selectedPreset === '' ? 'All' : selectedPreset}
                        hideFilter={false}
                        onChange={preset => updateURLParam('presetFilter', preset)}
                    />
                    <div className="flex flex-row gap-2 items-center">
                        <Checkbox
                            className='w-6 h-6 rounded-none border text-neutral-800  dark:text-neutral-950  bg-white dark:bg-neutral-950 border-black dark:border-neutral-400  dark:ui-checked:border-neutral-400 focus:ring-black dark:focus:border-neutral-400  dark:ring-offset-gray-500 focus:ring-2'
                            checked={audioOnlyIsTicked}
                            onChange={(e) => {
                                setAudioOnlyIsTicked(e.target.checked);
                                updateURLParam('audioOnly', e.target.checked.toString());
                            }}/>                                                        
                        <Label>Audio sync&apos;ed only</Label>
                    </div>
                    {isAdmin && <div className="flex flex-row gap-2 items-center">
                        <Checkbox
                            className='w-6 h-6 rounded-none border text-neutral-800 dark:text-neutral-950   bg-white dark:bg-neutral-950 border-black dark:border-neutral-400 dark:ui-checked:border-neutral-400 focus:ring-black dark:focus:border-neutral-400  dark:ring-offset-gray-500 focus:ring-2'
                            checked={includePrivateIsTicked}
                            onChange={(e) => {
                                setIncludePrivateIsTicked(e.target.checked);
                                updateURLParam('adminOverride', e.target.checked.toString());
                            }}/>                            
                        <Label>Include private vids (admin only)</Label>
                    </div>}
                    {isAdmin && <div className="flex flex-row gap-2 items-center">
                        <Checkbox
                            className='w-6 h-6 rounded-none border text-neutral-800 dark:text-neutral-950   bg-white dark:bg-neutral-950 border-black dark:border-neutral-400 dark:ui-checked:border-neutral-400 focus:ring-black dark:focus:border-neutral-400  dark:ring-offset-gray-500 focus:ring-2'
                            checked={allStatusesIsTicked}
                            onChange={(e) => {
                                setAllStatusesIsTicked(e.target.checked);
                                updateURLParam('statusFilter', (e.target.checked ? ALL_STATUSES.join(',') : ''));
                            }}/>
                        <Label>Show all statuses (admin only)</Label>
                    </div>}
                    {loading && <StyledSpinner  className='self-center w-3 h-3 mx-auto' />}        
                </div>
            )}
        </div>
    );
}